/**
 * Quais rotinas preventivas entram no aviso da tela inicial.
 *
 * Papel: aqui ficam `JANELA_DE_VENCIMENTO_DE_PREVENTIVAS_EM_DIAS`, a
 * antecedência com que uma rotina entra no aviso, e
 * `apurarVencimentosDePreventivas`, que mapeia cada rotina para o formato do
 * aviso e entrega a `apurarVencimentos`, de `shared/lib/vencimento.ts`.
 *
 * Como usar: este arquivo sabe o que é de manutenção — o rótulo que a linha
 * leva no aviso, a rota onde ela se resolve e a janela das preventivas, que é
 * mais curta do que a de documentos e apólices. A contagem de dias e o
 * descarte de quem não tem data são de `shared/`, e valem igual para os três
 * módulos; reescrevê-los aqui faria o aviso passar a mostrar dois critérios ao
 * mesmo tempo. A janela é a exceção, e fica aqui justamente para que mudar a
 * de documentos e apólices não arraste junto a das preventivas.
 *
 * O `referente` é a casa, e não uma pessoa: uma rotina de manutenção pertence ao
 * lugar. É a razão de aquele campo não se chamar `pessoa`.
 *
 * O `status` não entra na conta, e a consequência é deliberada: uma rotina
 * marcada `em_andamento` cuja próxima visita já passou **aparece no aviso**. Uma
 * rotina que está sendo tratada e mesmo assim venceu é o caso que mais precisa
 * de atenção, não o que menos precisa.
 */
import { type Preventiva } from '@concierge/shared';

import { type ItemVencendo, apurarVencimentos } from '@/shared/lib/vencimento';

/**
 * Quantos dias antes da próxima visita uma rotina preventiva passa a ser
 * anunciada.
 *
 * São 90 dias, mais curtos do que os 183 de documentos e apólices. A tabela de
 * preventivas não lê esta janela: ela só marca a visita vencida, como a origem;
 * a antecedência é do aviso da tela inicial.
 */
export const JANELA_DE_VENCIMENTO_DE_PREVENTIVAS_EM_DIAS = 90;

/**
 * Apura as rotinas preventivas que entram no aviso.
 *
 * @param preventivas - As rotinas como a API as devolve.
 * @param hojeIso - A data de hoje em `AAAA-MM-DD`, de quem chama.
 * @returns Os itens a anunciar, na ordem em que vieram; quem ordena é o
 *   `PopupVencimentos`.
 */
export function apurarVencimentosDePreventivas(
  preventivas: readonly Preventiva[],
  hojeIso: string,
): ItemVencendo[] {
  return apurarVencimentos(
    preventivas.map((preventiva) => ({
      id: preventiva.id,
      referente: preventiva.residencia.apelido,
      rotulo: [preventiva.elemento, preventiva.atividade]
        .filter((parte) => parte !== null)
        .join(' — '),
      validade: preventiva.proximaVisita,
      destino: '/manutencoes',
    })),
    hojeIso,
    JANELA_DE_VENCIMENTO_DE_PREVENTIVAS_EM_DIAS,
  );
}
